import { FacebookIcon, InstagramIcon } from "@/shared/components/SocialIcons";
import { useState } from "react";
import { Button } from "@a2z/ui";
import { useLocale } from "@/app/locale";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { isIosCompanionApp } from "@/lib/native/runtime";
import {
  metaIdentityErrorMessage,
  metaRequest,
  useMetaWrite,
  type MetaIdentityProvider,
} from "@/shared/meta/api";
import { useMetaAuthorization } from "@/shared/meta/useMetaAuthorization";

export function MetaLoginButtons() {
  const locale = useLocale();
  const t = useTranslations();
  const authorization = useMetaAuthorization();
  const [failure, setFailure] = useState<{
    provider: MetaIdentityProvider;
    error: unknown;
  } | null>(null);
  const start = useMetaWrite("", ({ provider, signal }: { provider: MetaIdentityProvider; signal: AbortSignal }) =>
    metaRequest<{ authorizationUrl: string; nativeAttemptId?: string }>(
      "/public/meta/auth/start",
      "POST",
      { locale, provider },
      { signal },
    ),
    { invalidateOnSuccess: false },
  );
  if (isIosCompanionApp()) return null;
  return (
    <div className="space-y-4">
      {(["FACEBOOK", "INSTAGRAM"] as const).map((provider) => (
        <div key={provider} className="space-y-2">
          <Button
            className="w-full gap-2"
            variant="outline"
            size="touch"
            leadingIcon={
              provider === "FACEBOOK" ? <FacebookIcon /> : <InstagramIcon />
            }
            disabled={Boolean(authorization.pendingProvider)}
            isLoading={authorization.pendingProvider === provider}
            onClick={() => {
              setFailure(null);
              void authorization
                .start(provider, (signal) => start.mutateAsync({ provider, signal }))
                .catch((error) => setFailure({ provider, error }));
            }}
          >
            {t(
              provider === "FACEBOOK"
                ? keys.meta.identityLogin
                : keys.meta.instagramIdentityLogin,
            )}
          </Button>
          {provider === "INSTAGRAM" ? (
            <p className="text-center text-xs text-[color:var(--text-muted)]">
              {t(keys.meta.instagramIdentityLoginHint)}
            </p>
          ) : null}
          {failure?.provider === provider ? (
            <p role="alert" className="text-sm">
              {metaIdentityErrorMessage(failure.error, provider)}
            </p>
          ) : null}
        </div>
      ))}
      {authorization.canCancel && authorization.pendingProvider ? (
        <Button
          className="w-full"
          variant="ghost"
          size="touch"
          onClick={() => authorization.cancel(authorization.pendingProvider!)}
        >
          {t(keys.common.cancel)}
        </Button>
      ) : null}
    </div>
  );
}
